import type {
  CheckedFilter, QueryObject, FetchDataOptions, Filter, FilterValue
} from "../../../site/parts/listSearch/type.d";
import { forceArray } from "/lib/helsecore/forceArray";

/**
 * Compute initial filter values from filter lists and URL params.
 */
export function getInitialFilterValues(
  filterLists: Filter[],
  filterFromParams: string[]
): CheckedFilter[] {
  if (filterFromParams) {
    return filterLists.map((filterObj) => ({
      field: filterObj.field,
      values: filterObj.filterList
        .filter((filterValue: FilterValue) => filterValue.isCheckedInitially)
        .map((filterValue: FilterValue) => filterValue.value)
    }));
  }

  return filterLists.map((filterObj) => ({
    field: filterObj.field,
    values: []
  }));
}

/**
 * Build a filter query string from checked filter values.
 * Values within a field are joined with OR, fields are joined with AND.
 */
export function buildFilterQuery(filters: CheckedFilter[]): string {
  const filterStringQueries = filters
    .filter((filter) => filter.values?.length > 0)
    .map((filter) => {
      const fieldQuery = filter.values
        .map((value) => `${filter.field} = '${value}'`)
        .join(" OR ");
      return `(${fieldQuery})`;
    });

  if (filterStringQueries.length <= 1) {
    return filterStringQueries[0] ?? "";
  }

  return filterStringQueries.join(" AND ");
}

/**
 * Determine sort order: use score-based sort when there is a search query
 * and results are not grouped.
 */
export function getSortByScore(searchQuery: string, groupByField?: string): string {
  if (searchQuery === "" || groupByField) {
    return "";
  }
  return "_score DESC";
}

/**
 * Build the full service URL with query parameters using URLSearchParams.
 */
export function buildServiceUrl(
  serviceUrl: string,
  queryObject: QueryObject,
  groupByField?: string,
  useNormerendeCollection?: boolean,
  options: FetchDataOptions = {}
): string {
  const params = new URLSearchParams();
  const { searchTerm, filterQuery, sortByScore } = options;

  // Handle pagination
  params.set("start", String(queryObject.start));
  params.set("count", String(queryObject.count));

  if (sortByScore || queryObject.sort) {
    params.set("sort", String(sortByScore || queryObject.sort));
  }

  if (queryObject.query) {
    const queryPathsString = forceArray(queryObject.query)
      .map((query: string) => `${query}`)
      .join(" OR ");
    params.set("query", queryPathsString);
  }

  if (queryObject.tabQuery) {
    params.set("tabQuery", String(queryObject.tabQuery));
  }

  if (queryObject.contentTypes) {
    params.set("contentTypes", queryObject.contentTypes.join(","));
  }

  if (queryObject.filters) {
    params.set("filterObject", JSON.stringify(queryObject.filters));
  }

  if (searchTerm) {
    params.set("searchTerm", String(searchTerm));
  }

  if (filterQuery) {
    params.set("filter", String(filterQuery));
  }

  if (groupByField) {
    params.set("groupBy", String(groupByField));
  }

  if (useNormerendeCollection) {
    params.set("useNormerendeCollection", "true");
  }

  return `${serviceUrl}?${params.toString()}`;
}
